import uuidv4 from 'uuid/v4';

import {
  IPrepaidsMergeInput,
  IPrepaidsMergeMutation,
  IsoCountryCode,
  PrepaidsMergeDocument,
} from 'generated/rbi-graphql';
import { getFdProxyAndUrl } from 'remote/api/get-fd-proxy-and-url';
import { IApplePayDetails, ICredit, IGooglePayDetails, IPrepaid } from 'state/payment/types';
import { encryptCredit } from 'utils/encryption';
import { isLocalDev } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { IPaymentPayload } from 'utils/payment';

import { QUERY_TYPE } from '../constants';
import { FirstDataError } from '../exceptions';

interface IAccount {
  fdAccountId: string;
  credit: ICredit | null;
  prepaid: IPrepaid | null;
}

interface IGetUserAccountsQueryResponse {
  userAccounts: {
    count: number;
    accounts: IAccount[];
  };
}

interface IPrepaidsReload {
  countryCode: string;
  fundValue: number;
  fdAccountId?: string | null | undefined;
  prepaidFdAccountId?: string | null;
  sessionId: string;
  applePayDetails?: IApplePayDetails;
  googlePayDetails?: IGooglePayDetails;
}

export interface IMergePrepaidCardsArgs {
  destinationFdAccountId: string | null;
  sourceCardNumber: string;
  sourcePin: string;
}

export interface IMergePrepaidCardsResponse {
  prepaidsMerge: {
    currentBalance: number;
  };
  errors?: Error[];
}

export interface IAddGiftAccountArgs {
  fdAccountId?: string;
  applePayDetails?: IApplePayDetails;
  googlePayDetails?: IGooglePayDetails;
  fundValue: number;
  sessionId: string;
}

export interface IAddGiftAccountResponse {
  addGiftAccount: {
    fdAccountId: string;
    prepaid: {
      currentBalance: number;
    };
  };
  errors?: Error[];
}

/**
 * The First Data endpoint doesn't support CORS for localhost,
 * so during local dev we hit a proxy on our dev server.
 * Stable environments (dev, staging, prod) can hit the API directly from the browser.
 */
export const getFirstDataBaseUrl = (country?: IsoCountryCode) => {
  const [proxy, firstData] = getFdProxyAndUrl(country);
  const reg = LocalStorage.getItem(StorageKeys.REGION);
  const regEMEA = ['DE', 'ZA', 'GB', 'NZ', 'CH'];
  const isEMEA = regEMEA.includes(reg);
  // NOTE: cypress-v2 test suite requirement
  // Use the same proxy as local development when Cypress is running.
  // Allows Cypress in CI to mimic the recorded request patterns.
  if (isEMEA && window.Cypress) {
    return firstData;
  }
  if (isLocalDev || window.Cypress) {
    return proxy;
  }
  return firstData;
};

interface IGetNonce {
  account: {
    // @todo validate type
    credit: {
      cardNumber: string;
      expiryDate: {
        month: string;
        year: string;
      };
      securityCode: string;
    };
    type: string;
  };
  deviceInfo: {
    id: string;
    kind: string;
  };
  referenceToken: {
    tokenType: string;
  };
  fdCustomerId?: string;
}

export const getNonce = async (
  credit: IPaymentPayload,
  fdPublicKey: string,
  fdApiKey: string,
  fdAccessToken: string,
  fdCustomerId: string | null,
  isoCountryCode?: IsoCountryCode,
  onlySendPostalCode?: boolean,
  algorithm?: string | null
) => {
  const encryptedCredit = await encryptCredit({
    credit,
    fdPublicKey,
    algorithm,
  });
  // billing address sent to First Data may only require postal code
  const postalCode = credit.billingAddress.postalCode;
  const billingAddress = onlySendPostalCode ? { postalCode } : credit.billingAddress;

  const encryptedCreditWithCorrectBilling = {
    ...encryptedCredit,
    billingAddress,
  };

  const headers = {
    Authorization: `Bearer ${fdAccessToken}`,
    'Content-Type': 'application/json',
    Timestamp: (new Date().getTime() as unknown) as string,
    'Client-Request-Id': uuidv4(),
    'Api-Key': fdApiKey,
  };

  const body: IGetNonce = {
    account: {
      credit: encryptedCreditWithCorrectBilling,
      type: 'CREDIT',
    },
    deviceInfo: {
      id: uuidv4(),
      kind: 'web',
    },
    referenceToken: {
      tokenType: 'CLAIM_CHECK_NONCE',
    },
  };

  if (fdCustomerId) {
    body.fdCustomerId = fdCustomerId;
  }

  const firstDataBaseUrl = getFirstDataBaseUrl(isoCountryCode);
  const response = await fetch(`${firstDataBaseUrl}/ucom/v1/account-tokens`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (response.ok) {
    return response;
  }

  throw new FirstDataError('Error getting nonce', new Error(response.statusText));
};

export const mergePrepaidCards = ({
  destinationFdAccountId,
  sourceCardNumber,
  sourcePin,
}: IPrepaidsMergeInput) => {
  const destinationInput = !destinationFdAccountId ? {} : { destinationFdAccountId };
  return {
    queryType: QUERY_TYPE.LambdaGraphqlQuery,
    query: PrepaidsMergeDocument,
    variables: {
      input: {
        ...destinationInput,
        sourceCardNumber: sourceCardNumber.replace(/\s/g, ''),
        sourcePin,
      },
    },
    then: (data: IPrepaidsMergeMutation) => data.prepaidsMerge,
    catch: (err: Error) => {
      throw new FirstDataError(
        'An error occurred while transferring the balance of your TimCards',
        err
      );
    },
  };
};
